/**
 * Print-resolution tagging for exported stills.
 *
 * The Canvas export panel has offered DPI pills since it shipped, and the value
 * only ever reached the download FILENAME — while the blurb underneath claimed
 * it was "tagged for print". PNG carries resolution in a `pHYs` chunk and TIFF
 * in tags 282/283/296; both are written now. JPEG and WebP are left alone (the
 * UI says so rather than pretending).
 */

const PPM_PER_INCH = 1 / 0.0254; // pixels-per-metre in one inch
const PNG_SIG = [137, 80, 78, 71, 13, 10, 26, 10];

let crcTable: Uint32Array | null = null;
function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** TIFF IFD entries for a print resolution — pass as UTIF.encodeImage's 4th arg. */
export const tiffDpiTags = (dpi: number) => ({ t282: [dpi], t283: [dpi], t296: [2] }); // X, Y, unit=inch

/**
 * Return the PNG with a `pHYs` chunk stating `dpi`, replacing one if present.
 * Anything that isn't a PNG comes back untouched.
 */
export async function pngWithDpi(png: Blob, dpi: number): Promise<Blob> {
  const src = new Uint8Array(await png.arrayBuffer());
  if (src.length < 8 || PNG_SIG.some((b, i) => src[i] !== b)) return png;

  const ppm = Math.round(dpi * PPM_PER_INCH);
  const chunk = new Uint8Array(21); // 4 length + 4 type + 9 data + 4 crc
  const cv = new DataView(chunk.buffer);
  cv.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
  cv.setUint32(8, ppm);
  cv.setUint32(12, ppm);
  chunk[16] = 1; // unit specifier: metre
  cv.setUint32(17, crc32(chunk.subarray(4, 17)));

  // Walk the chunk list rather than assuming IHDR sits at a fixed offset, and
  // drop any pHYs already there so the file never ends up with two.
  const view = new DataView(src.buffer, src.byteOffset, src.byteLength);
  const out: Uint8Array[] = [src.subarray(0, 8)];
  let at = 8;
  let placed = false;
  while (at + 8 <= src.length) {
    const len = view.getUint32(at);
    const type = String.fromCharCode(src[at + 4], src[at + 5], src[at + 6], src[at + 7]);
    const end = at + 12 + len;
    if (type !== "pHYs") out.push(src.subarray(at, Math.min(end, src.length)));
    if (type === "IHDR") { out.push(chunk); placed = true; }
    if (type === "IEND") break;
    at = end;
  }
  return placed ? new Blob(out as BlobPart[], { type: "image/png" }) : png;
}
